import React from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { shortenandHoverString } from '../../Components/helper';

/** Above this many entries the cell gets its own scroll box instead of stretching the table row. */
const SCROLL_AFTER_ENTRIES = 10;
/** Characters before a cell value is truncated into a hover tooltip. */
const VALUE_MAX_CHARS = 40;
/**
 * The combination header gets more room than a cell value: it names the row, and it carries the
 * product, the colourway as `name | reference`, and every axis. Cut at 40 it loses the reference.
 */
const COMBINATION_MAX_CHARS = 70;

/**
 * Renders the From or To cell of one Product Identifier history entry (BM-9941).
 *
 * A sibling renderer rather than a branch inside `DevelopmenorderFields`, following the same
 * pattern the DAM section uses. Two reasons it cannot go through that component: its generic
 * fallback puts every label through lodash `startCase`, which would turn the combination label
 * `W1042 | Navy | 12` into `W1042 Navy 12`; and its field allow-list routes anything named
 * `Product Identifier` into the legacy whole-array renderer, which would crash on these entries.
 *
 * The From and To columns are two independent renders of this component with a different
 * `colType`. They only line up because both iterate the same array in the same order and emit
 * exactly one line per entry — a side with no value prints a placeholder rather than skipping.
 */
function ProductIdentifierFields({ historyFieldsData, colType }) {
  const { t } = useTranslation('common');

  if (!isArray(historyFieldsData) || historyFieldsData.length === 0) {
    return <span>--</span>;
  }

  const body = historyFieldsData.map((entry, index) => {
    const previous = historyFieldsData[index - 1];
    const startsGroup =
      entry?.changeType !== 'summary' && previous?.combinationKey !== entry?.combinationKey;
    const value = String(entry?.[colType] ?? '');

    if (entry?.changeType === 'summary') {
      return (
        <div className="px-2 py-1" key={`summary-${index}`}>
          <i>{String(entry?.rhs ?? '')}</i>
        </div>
      );
    }

    return (
      <div className="px-2 py-1" key={`${entry?.combinationKey}-${entry?.field}-${index}`}>
        {startsGroup && (
          <div className="column-title">
            {shortenandHoverString(String(entry?.combination ?? ''), COMBINATION_MAX_CHARS) || '--'}
            {entry?.changeType === 'row-added' && ` (${t('PRODUCT_HISTORY.PRODUCT_IDENTIFIER.COMBINATION_ADDED')})`}
            {entry?.changeType === 'row-removed' && ` (${t('PRODUCT_HISTORY.PRODUCT_IDENTIFIER.COMBINATION_REMOVED')})`}
          </div>
        )}
        <b>{String(entry?.field ?? '')}</b>: {shortenandHoverString(value, VALUE_MAX_CHARS) || '--'}
      </div>
    );
  });

  return historyFieldsData.length > SCROLL_AFTER_ENTRIES ? (
    <span className="position-static">
      <PerfectScrollbar className="inner-scroll">{body}</PerfectScrollbar>
    </span>
  ) : (
    <span>{body}</span>
  );
}

ProductIdentifierFields.propTypes = {
  historyFieldsData: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      lhs: PropTypes.string,
      rhs: PropTypes.string,
      combination: PropTypes.string,
      combinationKey: PropTypes.string,
      changeType: PropTypes.string,
    })
  ),
  colType: PropTypes.string,
};

ProductIdentifierFields.defaultProps = {
  historyFieldsData: [],
  colType: 'lhs',
};

export default ProductIdentifierFields;
